import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Modal from 'react-modal';
import Select from 'react-select';
import { getCSRFToken, ROOT_URL_V2 } from '../../../config';
import { ErrorMessage } from '../../common';
import { getErrorMessages } from '../../../utils';
import LoadingSpinner from '../../common/LoadingSpinner';
import { TPageNotification } from '../../../types';


type TModalConfirmCancelWorkerProps = {
    isOpen: boolean;
    isLoading: boolean;
    handleClose: () => void;
    handleConfirm: () => void,
    fields: {
        freelancer: number,
        status: 'CA' | 'FC',
        is_silent: boolean,
    },
    seriesId: number;
    statusOptions: {
        label: string;
        value: string;
    }[];
    freelancerName: string;
}

export function ModalConfirmCancelWorker({
    isOpen,
    isLoading,
    handleClose,
    handleConfirm,
    fields,
    seriesId,
    statusOptions,
    freelancerName,
}: TModalConfirmCancelWorkerProps) {

    return (
        <Modal
            className="cancel-worker-modal"
            isOpen={isOpen}
            onRequestClose={handleClose}
            contentLabel="Confirm shift"
            ariaHideApp={false}
        >
            <p className="modal-title">Cancel worker</p>
            <div className="cancel-worker-body">
                <p>Are you sure you want to remove <b>{freelancerName}</b>{' '} from all future dates in ongoing job <b>{seriesId}</b>?</p>
                <p className="margin-top-10">Reason: <b>{statusOptions.find(item => item.value === fields.status).label}</b></p>
                <p>Silent? <b>{fields.is_silent ? 'Yes' : 'No'}</b></p>
            </div>

            <button
                className="continue btn--round-big"
                onClick={handleConfirm}
                type="button"
                disabled={isLoading}
            >
                {isLoading ? (
                    <LoadingSpinner
                        className="cancel-worker-spinner"
                        backgroundColor="white"
                    />
                ) : (
                    'CONFIRM'
                )}
            </button>
            {!isLoading && (
                <button className="back" onClick={handleClose} type="button">
                    CANCEL
                </button>
            )}
        </Modal>
    );
}


type TModalCancelWorkerProps = {
    notification: TPageNotification;
    reloadData: () => void;
    handleClose: (modalName: string) => void;
    isOpen: boolean;
    seriesId: number;
}

export function ModalCancelWorker({
    notification,
    reloadData,
    handleClose,
    isOpen,
    seriesId,
}: TModalCancelWorkerProps) {

    const [fields, setFields] = useState({
        freelancer: null,
        status: null,
        is_silent: false,
    });
    const [isValid, setIsValid] = useState(false);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalConfirmCancelWorkerOpen, setIsModalConfirmCancelWorkerOpen] = useState(false);
    const [freelancerName, setFreelancerName] = useState(null);
    const [freelancers, setFreelancers] = useState([]);

    useEffect(() => {
        async function getOptions() {
            if (isOpen) {
                try {
                    const response = await axios({
                        method: 'GET',
                        url: `${ROOT_URL_V2}series/${seriesId}/freelancers/`,
                        headers: {
                            'X-CSRFToken': getCSRFToken(),
                        },
                    });
                    setFreelancers(response.data.content);
                } catch (err) {
                    setError(getErrorMessages(err));
                }
            }
        } getOptions();
    }, [isOpen, seriesId]);

    useEffect(() => {
        if (fields.status && fields.status !== 'CA' && fields.is_silent) {
            setFields({
                ...fields,
                is_silent: false,
            });
        }
    }, [fields.status]);

    const freelancerOptions = freelancers.map(freelancer => ({
        label: freelancer.full_name,
        value: freelancer.id
    }));
    const statusOptions = [
        { label: 'Client cancelled', value: 'CA' },
        { label: 'Worker cancelled', value: 'FC' },
    ];

    const handleOpenModalConfirmCancelWorker = (): void => {
        setIsModalConfirmCancelWorkerOpen(true);
    };

    const handleCloseModalConfirmCancelWorker = (): void => {
        setIsModalConfirmCancelWorkerOpen(false);
    };

    const handleSelect = (event: any, field: 'freelancer' | 'status') => {
        setFields({
            ...fields,
            [field]: event.value,
        });
    };

    const handleCheckbox = (event: any, field: 'is_silent') =>
        setFields({
            ...fields,
            [field]: event.target.checked,
        });


    useEffect(() => {
        const name = freelancers.find(freelancer => freelancer.id === fields.freelancer)?.full_name;
        setFreelancerName(name);
    }, [fields.freelancer]);


    const fieldsAreValid = (): void => {
        if (fields.freelancer === null || fields.status === null) {
            setIsValid(false);
        } else {
            setIsValid(true);
        }
    };

    useEffect(() => {
        fieldsAreValid();
    }, [fields]);

    const handleContinue = (): void => {
        setError(null);
        handleOpenModalConfirmCancelWorker();
    };

    const handleConfirm = (): void => {
        if (isValid) {
            setIsLoading(true);

            axios({
                method: 'patch',
                url: `${ROOT_URL_V2}series/${seriesId}/cancel/`,
                data: fields,
                headers: {
                    'X-CSRFToken': getCSRFToken(),
                },
            }).then(() => {
                setIsLoading(false);
                handleCloseModalConfirmCancelWorker();
                handleClose('CancelWorker');

                notification.success(
                    'Success',
                    `${freelancerName} successfully removed from all future dates`
                );

                reloadData();
            }).catch(err => {
                setIsLoading(false);
                setError(getErrorMessages(err));
                handleCloseModalConfirmCancelWorker();
            });

        }
    };

    return (
        <Modal
            className="cancel-worker-modal"
            isOpen={isOpen}
            onRequestClose={() => handleClose('CancelWorker')}
            contentLabel="Confirm shift"
            ariaHideApp={false}
        >
            <p className="modal-title">Cancel worker</p>
            <div className="cancel-worker-body">
                <p className="cancel-worker-text">Select a worker to remove from the ongoing job <b>{seriesId}</b>:</p>

                <Select
                    name="select-freelancer"
                    value={fields.freelancer}
                    clearable={false}
                    onChange={event => {
                        handleSelect(event, 'freelancer');
                    }}
                    options={freelancerOptions}
                />
                <p className="cancel-worker-text margin-top-20">Select a reason:</p>
                <Select
                    name="select-status"
                    value={fields.status}
                    clearable={false}
                    onChange={event => {
                        handleSelect(event, 'status');
                    }}
                    options={statusOptions}
                />
                <div className="margin-top-20">
                    <label
                        className="cancel-worker-checkbox"
                        hidden={fields.status !== 'CA'}
                    >
                        <input
                            className="checkbox-purple"
                            type="checkbox"
                            checked={fields.is_silent}
                            onChange={event => {
                                handleCheckbox(event, 'is_silent');
                            }}
                        />
                        <span>Cancel silently</span>
                    </label>
                </div>
            </div>
            <button
                className="continue btn--round-big"
                onClick={handleContinue}
                type="button"
                disabled={!isValid}
            >
                CONTINUE
            </button>
            <button className="back" onClick={() => handleClose('CancelWorker')} type="button">
                CANCEL
            </button>

            {!!error && <ErrorMessage message={error}/>}

            {isModalConfirmCancelWorkerOpen &&
                <ModalConfirmCancelWorker
                    isOpen={isModalConfirmCancelWorkerOpen}
                    isLoading={isLoading}
                    handleClose={handleCloseModalConfirmCancelWorker}
                    handleConfirm={handleConfirm}
                    fields={fields}
                    seriesId={seriesId}
                    statusOptions={statusOptions}
                    freelancerName={freelancerName}
                />
            }
        </Modal>
    );
}
